import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Loader2,
  Copy,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Phone,
  Mail,
  Send,
  AlertTriangle,
  CheckCircle2,
  Package,
  Wrench,
  LineChart,
  Filter,
} from 'lucide-react';

const API = '/api';

const STATUS_LABEL = {
  new: { label: 'New', cls: 'bg-blue-100 text-blue-700' },
  contacted: { label: 'Contacted', cls: 'bg-amber-100 text-amber-700' },
  reviewed: { label: 'Reviewed', cls: 'bg-cyan-100 text-cyan-700' },
  packaged: { label: 'Packaged', cls: 'bg-indigo-100 text-indigo-700' },
  shipped: { label: 'Shipped', cls: 'bg-green-100 text-green-700' },
  cancelled: { label: 'Cancelled', cls: 'bg-rose-100 text-rose-700' },
  won: { label: 'Won', cls: 'bg-green-100 text-green-700' },
  lost: { label: 'Lost', cls: 'bg-muted text-muted-foreground' },
};

// Per-cta_type workflow: starter-kit has its own pipeline; installation/monitoring keep the generic one.
const STATUS_WORKFLOW = {
  start_package: ['new', 'reviewed', 'packaged', 'shipped', 'cancelled'],
  installation: ['new', 'contacted', 'won', 'lost'],
  monitoring: ['new', 'contacted', 'won', 'lost'],
};

const COMPAT_LABELS = {
  meter_type: {
    smart_p1: 'Slimme meter met P1-poort',
    smart_p1_unclear: 'Slimme meter, P1 onbekend',
    old_unclear: 'Oude meter / analoge meter',
    unknown: 'Weet ik niet',
  },
  phase_type: {
    one_phase: '1-fase aansluiting',
    three_phase: '3-fase aansluiting',
    unknown: 'Weet ik niet',
  },
  live_insight: {
    has_dashboard: 'Ik heb al live inzicht',
    no_dashboard: 'Nog geen live inzicht',
    unknown: 'Weet ik niet',
  },
  // Legacy — older leads still have solar_setup; keep readable in admin
  solar_setup: {
    panels_with_inverter: 'Panelen met omvormer aanwezig',
    panels_unknown_type: 'Panelen aanwezig, type onbekend',
    unknown: 'Geen idee',
    not_applicable: 'Niet van toepassing',
  },
};

const PREF_LABELS = {
  diy: 'Zelf installeren',
  professional: 'Installateur laten installeren',
  advice_first: 'Eerst advies',
};

const TYPE_META = {
  start_package: { label: 'Startpakket', icon: Package, cls: 'bg-primary/10 text-primary' },
  installation: { label: 'Installatie', icon: Wrench, cls: 'bg-orange-100 text-orange-700' },
  monitoring: { label: 'Monitoring', icon: LineChart, cls: 'bg-purple-100 text-purple-700' },
};

function fmtDate(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' });
  } catch {
    return iso;
  }
}

// Heuristic test-data classifier (Feb 2026). Used purely to HIDE rows in
// the admin UI by default — rows are NEVER deleted from MongoDB. The owner
// can toggle "Testdata tonen" to inspect them when debugging.
export function isTestLead(lead) {
  if (!lead) return false;
  const email = String(lead.email || '').toLowerCase().trim();
  if (!email) return false;
  if (email.includes('pytest')) return true;
  if (email.includes('test_rl')) return true;
  if (email.startsWith('test_')) return true;
  if (email.startsWith('test+')) return true;
  if (email.startsWith('test@')) return true;
  if (email.includes('@example.com') || email.includes('@example.org')) return true;
  if (email.includes('regtest@')) return true;
  // Free-text fixtures
  const name = String(lead.name || '').toLowerCase();
  if (name.includes('pytest') || name.startsWith('test_rl')) return true;
  // Source-side markers (sometimes funnel events leak into the lead doc)
  const utmSrc = String(lead.utm_source || '').toLowerCase();
  const utmCmp = String(lead.utm_campaign || '').toLowerCase();
  if (utmSrc === 'pytest' || utmCmp.includes('pytest')) return true;
  const sess = String(lead.session_id || '').toLowerCase();
  if (sess.includes('pytest')) return true;
  return false;
}

function copy(text) {
  if (!text) return;
  navigator.clipboard.writeText(text).then(() => toast.success('Gekopieerd')).catch(() => {});
}

function StatusBadge({ status }) {
  const s = STATUS_LABEL[status] || STATUS_LABEL.new;
  return (
    <span className={`inline-flex px-2 py-0.5 rounded-full text-[11px] font-medium ${s.cls}`} data-testid={`ec-lead-status-${status || 'new'}`}>
      {s.label}
    </span>
  );
}

function TypePill({ type }) {
  const meta = TYPE_META[type];
  if (!meta) return <span className="text-xs text-muted-foreground">{type}</span>;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${meta.cls}`}>
      <Icon className="w-3 h-3" /> {meta.label}
    </span>
  );
}

function EmailDiagnosticsPanel({ token }) {
  const [status, setStatus] = useState(null);
  const [statusLoading, setStatusLoading] = useState(false);
  const [runtime, setRuntime] = useState(null);
  const [runtimeLoading, setRuntimeLoading] = useState(false);
  const [runtimeError, setRuntimeError] = useState(null);
  const [showRuntime, setShowRuntime] = useState(false);
  const [testTo, setTestTo] = useState('');
  const [testLoading, setTestLoading] = useState(false);
  const [lastTestResult, setLastTestResult] = useState(null);

  const refresh = useCallback(async () => {
    setStatusLoading(true);
    try {
      const res = await axios.get(`${API}/energy-control/email-status`);
      setStatus(res.data);
    } catch {
      setStatus({ configured: false, issues: ['Status endpoint failed'] });
    } finally {
      setStatusLoading(false);
    }
  }, []);

  const fetchRuntime = useCallback(async () => {
    if (!token) return;
    setRuntimeLoading(true);
    setRuntimeError(null);
    try {
      const res = await axios.get(`${API}/admin/email/runtime-status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRuntime(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || 'Onbekende fout';
      setRuntimeError(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setRuntimeLoading(false);
    }
  }, [token]);

  useEffect(() => { refresh(); fetchRuntime(); }, [refresh, fetchRuntime]);

  const validateRecipient = (raw) => {
    const email = (raw || '').trim().toLowerCase();
    if (!email) return 'Vul een echt e-mailadres in';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'Ongeldig e-mailadres';
    const blocked = ['example.com', 'example.org', 'example.net', 'test.com', 'localhost'];
    const domain = email.split('@')[1] || '';
    if (blocked.includes(domain)) return `Verboden domein: ${domain}. Gebruik een echt e-mailadres.`;
    if (email === 'test_admin_ui@example.com') return 'Placeholder-adres niet toegestaan';
    return null;
  };

  const sendTest = async () => {
    const validationErr = validateRecipient(testTo);
    if (validationErr) {
      toast.error(validationErr);
      setLastTestResult({ ok: false, msg: validationErr });
      return;
    }
    setTestLoading(true);
    setLastTestResult(null);
    try {
      const res = await axios.post(
        `${API}/admin/email/test`,
        { to_email: testTo.trim() },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (res.data?.success) {
        const sentTo = res.data.sent_to || testTo.trim();
        const id = res.data.provider_id || '—';
        toast.success(`Testmail verzonden naar ${sentTo}`);
        setLastTestResult({
          ok: true,
          msg: `Testmail verzonden naar ${sentTo}. Resend ID: ${id}`,
          data: res.data,
        });
      } else {
        const err = res.data?.error || 'Onbekende fout';
        toast.error('Testmail mislukt');
        setLastTestResult({
          ok: false,
          msg: `Testmail kon niet worden verzonden: ${err}`,
          data: res.data,
        });
      }
      // Refresh runtime status after a send so timestamps stay accurate
      fetchRuntime();
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.response?.data?.message || err.message;
      const msg = typeof detail === 'string' ? detail : JSON.stringify(detail);
      toast.error(typeof detail === 'string' ? detail : 'Testmail mislukt');
      setLastTestResult({ ok: false, msg: `Testmail kon niet worden verzonden: ${msg}` });
    } finally {
      setTestLoading(false);
    }
  };

  const ok = status?.configured && (status?.issues || []).length === 0;
  const alert = !ok;
  const effectiveNoreply = runtime?.effective?.contains_noreply;
  const noreplyHits = runtime?.noreply_hits_in_env || [];
  const hasOverrideWarning = effectiveNoreply || noreplyHits.length > 0;

  return (
    <div
      className={`mb-5 rounded-lg border p-4 ${alert || hasOverrideWarning ? 'bg-amber-50 border-amber-200' : 'bg-emerald-50/60 border-emerald-200'}`}
      data-testid="ec-email-diagnostics"
    >
      <div className="flex items-start gap-3 mb-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${alert || hasOverrideWarning ? 'bg-amber-100' : 'bg-emerald-100'}`}>
          {alert || hasOverrideWarning ? <AlertTriangle className="w-5 h-5 text-amber-700" /> : <CheckCircle2 className="w-5 h-5 text-emerald-700" />}
        </div>
        <div className="flex-1 min-w-0">
          <div className={`text-[11px] uppercase tracking-wider font-medium ${alert || hasOverrideWarning ? 'text-amber-700' : 'text-emerald-700'}`}>
            E-mail diagnose
          </div>
          <div className="text-sm font-medium mt-0.5">
            {statusLoading
              ? 'Status wordt geladen…'
              : status?.configured
                ? `Sender: ${status.sender_email || '—'}`
                : 'Niet geconfigureerd'}
          </div>
          {status?.issues?.length > 0 && (
            <ul className="mt-1 text-xs text-amber-900 list-disc list-inside" data-testid="ec-email-issues">
              {status.issues.map((iss, i) => <li key={i}>{iss}</li>)}
            </ul>
          )}
          {hasOverrideWarning && (
            <div className="mt-1 text-xs text-amber-900 font-medium" data-testid="ec-email-override-warning">
              {effectiveNoreply
                ? '⚠ De effectieve from/reply_to bevat "noreply". Controleer de runtime env-variabelen hieronder.'
                : `⚠ ${noreplyHits.length} env-variabele(n) bevat(ten) "noreply". Zie runtime snapshot hieronder.`}
            </div>
          )}
          {status?.sender_domain && !alert && !hasOverrideWarning && (
            <div className="text-xs text-muted-foreground mt-1">
              Domein <span className="font-mono">{status.sender_domain}</span> moet geverifieerd zijn in Resend voor live sends.
            </div>
          )}
        </div>
        <button
          onClick={() => { refresh(); fetchRuntime(); }}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs border border-border rounded-md bg-white hover:bg-muted"
          data-testid="ec-email-status-refresh"
        >
          <RefreshCw className={`w-3 h-3 ${statusLoading || runtimeLoading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Runtime environment snapshot — production debug surface */}
      <div className="border-t border-amber-200/40 pt-3 mt-1">
        <button
          onClick={() => setShowRuntime((v) => !v)}
          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-medium text-muted-foreground hover:text-primary"
          data-testid="ec-email-runtime-toggle"
        >
          {showRuntime ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          Runtime environment snapshot
          {runtime?.server_started_at && (
            <span className="ml-1 text-muted-foreground normal-case tracking-normal">
              (server gestart: {fmtDate(runtime.server_started_at)})
            </span>
          )}
        </button>

        {showRuntime && (
          <div className="mt-2 space-y-3" data-testid="ec-email-runtime-panel">
            {runtimeLoading && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                <Loader2 className="w-3 h-3 animate-spin" /> Runtime-variabelen worden geladen…
              </div>
            )}
            {runtimeError && (
              <div className="text-xs bg-rose-50 border border-rose-200 rounded-md px-3 py-2 text-rose-900">
                Kon runtime status niet laden: {runtimeError}
              </div>
            )}
            {runtime && (
              <>
                <div className="text-xs bg-white border border-border rounded-md">
                  <div className="px-3 py-2 border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
                    Effective (zoals doorgegeven aan Resend)
                  </div>
                  <dl className="px-3 py-2 grid grid-cols-[140px_1fr] gap-x-3 gap-y-1 font-mono text-[12px] break-all">
                    <dt className="text-muted-foreground">from</dt>
                    <dd data-testid="ec-runtime-effective-from">{runtime.effective?.from || '∅'}</dd>
                    <dt className="text-muted-foreground">reply_to</dt>
                    <dd data-testid="ec-runtime-effective-reply-to">{runtime.effective?.reply_to || '∅'}</dd>
                    <dt className="text-muted-foreground">sender_bare</dt>
                    <dd>{runtime.effective?.sender_bare || '∅'}</dd>
                    <dt className="text-muted-foreground">quotes_from</dt>
                    <dd>{runtime.effective?.quotes_from || '∅'}</dd>
                    <dt className="text-muted-foreground">quotes_reply_to</dt>
                    <dd>{runtime.effective?.quotes_reply_to || '∅'}</dd>
                    <dt className="text-muted-foreground">contains_noreply</dt>
                    <dd className={runtime.effective?.contains_noreply ? 'text-rose-600 font-semibold' : 'text-emerald-700'}>
                      {String(!!runtime.effective?.contains_noreply)}
                    </dd>
                  </dl>
                </div>

                <div className="text-xs bg-white border border-border rounded-md">
                  <div className="px-3 py-2 border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
                    Raw env vars (process.env)
                  </div>
                  <dl className="px-3 py-2 grid grid-cols-[140px_1fr] gap-x-3 gap-y-1 font-mono text-[12px] break-all">
                    {Object.entries(runtime.raw_env || {}).map(([k, v]) => (
                      <div key={k} className="contents">
                        <dt className="text-muted-foreground">{k}</dt>
                        <dd data-testid={`ec-runtime-raw-${k}`}>
                          {v == null ? <span className="text-rose-400">not set</span> : String(v)}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </div>

                {Object.keys(runtime.all_email_env_vars || {}).length > 0 && (
                  <div className="text-xs bg-white border border-border rounded-md">
                    <div className="px-3 py-2 border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
                      All email-related env vars (full scan)
                    </div>
                    <dl className="px-3 py-2 grid grid-cols-[160px_1fr] gap-x-3 gap-y-1 font-mono text-[12px] break-all">
                      {Object.entries(runtime.all_email_env_vars).map(([k, v]) => (
                        <div key={k} className="contents">
                          <dt className="text-muted-foreground">{k}</dt>
                          <dd>{String(v)}</dd>
                        </div>
                      ))}
                    </dl>
                  </div>
                )}

                <div className={`text-xs rounded-md border px-3 py-2 ${noreplyHits.length ? 'bg-rose-50 border-rose-200 text-rose-900' : 'bg-emerald-50 border-emerald-200 text-emerald-900'}`} data-testid="ec-runtime-noreply-hits">
                  <div className="font-semibold mb-1">
                    {noreplyHits.length === 0
                      ? '✓ Geen enkele env-variabele bevat "noreply"'
                      : `⚠ ${noreplyHits.length} env-variabele(n) bevatten "noreply":`}
                  </div>
                  {noreplyHits.length > 0 && (
                    <ul className="list-disc list-inside space-y-0.5 font-mono">
                      {noreplyHits.map((hit, i) => (
                        <li key={i}><strong>{hit.env_var}</strong> = {hit.value}</li>
                      ))}
                    </ul>
                  )}
                </div>

                {/* Customer-facing email links (Phase 8.x) */}
                {runtime.email_links && (
                  <div
                    className={`text-xs bg-white border rounded-md ${runtime.email_links.app_base_url_unsafe ? 'border-amber-300' : 'border-border'}`}
                    data-testid="ec-runtime-email-links"
                  >
                    <div className="px-3 py-2 border-b border-border text-[11px] uppercase tracking-wider text-muted-foreground">
                      Customer-facing email links
                      {runtime.email_links.app_base_url_unsafe && (
                        <span className="ml-2 text-amber-700 normal-case tracking-normal font-semibold">
                          ⚠ APP_BASE_URL env carries preview/localhost — safety pin actief
                        </span>
                      )}
                    </div>
                    <dl className="px-3 py-2 grid grid-cols-[170px_1fr] gap-x-3 gap-y-1 font-mono text-[12px] break-all">
                      <dt className="text-muted-foreground">customer_base_url</dt>
                      <dd className="text-emerald-700 font-semibold" data-testid="ec-runtime-customer-base-url">
                        {runtime.email_links.customer_base_url || '∅'}
                      </dd>
                      <dt className="text-muted-foreground">APP_BASE_URL (env)</dt>
                      <dd className={runtime.email_links.app_base_url_unsafe ? 'text-amber-700' : ''} data-testid="ec-runtime-app-base-url-env">
                        {runtime.email_links.app_base_url_env || <span className="text-rose-400">not set</span>}
                      </dd>
                      <dt className="text-muted-foreground">production_domain</dt>
                      <dd>{runtime.email_links.production_domain}</dd>
                      <dt className="text-muted-foreground">sample report link</dt>
                      <dd data-testid="ec-runtime-sample-report-url">
                        <a
                          href={runtime.email_links.sample_report_url.replace('<intake_id>', runtime.email_links.last_generated?.last_intake_id || 'sample')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary underline"
                        >
                          {runtime.email_links.sample_report_url}
                        </a>
                      </dd>
                      <dt className="text-muted-foreground">sample starter-kit link</dt>
                      <dd data-testid="ec-runtime-sample-starter-url">
                        <a
                          href={runtime.email_links.sample_starter_kit_url.replace('<intake_id>', runtime.email_links.last_generated?.last_intake_id || 'sample')}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary underline"
                        >
                          {runtime.email_links.sample_starter_kit_url}
                        </a>
                      </dd>
                    </dl>
                    {runtime.email_links.last_generated?.last_built_at && (
                      <div className="px-3 py-2 border-t border-border text-[11px]">
                        <div className="uppercase tracking-wider text-muted-foreground mb-1">
                          Laatst gegenereerde links ({runtime.email_links.last_generated.last_context})
                        </div>
                        <div className="font-mono text-[12px] break-all" data-testid="ec-runtime-last-report-url">
                          Report: <a href={runtime.email_links.last_generated.last_report_url} target="_blank" rel="noopener noreferrer" className="text-primary underline">{runtime.email_links.last_generated.last_report_url}</a>
                        </div>
                        <div className="font-mono text-[12px] break-all mt-0.5" data-testid="ec-runtime-last-starter-url">
                          Starter-kit: <a href={runtime.email_links.last_generated.last_starter_kit_url} target="_blank" rel="noopener noreferrer" className="text-primary underline">{runtime.email_links.last_generated.last_starter_kit_url}</a>
                        </div>
                        <div className="text-muted-foreground mt-1">
                          Gebouwd op: {fmtDate(runtime.email_links.last_generated.last_built_at)} · intake: <span className="font-mono">{runtime.email_links.last_generated.last_intake_id}</span>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <div className="text-[11px] text-muted-foreground">
                  Deployment timestamp: <span className="font-mono">{runtime.server_started_at}</span>
                  {' · '}Now: <span className="font-mono">{runtime.now}</span>
                  {' · '}CWD: <span className="font-mono">{runtime.python_cwd}</span>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      <div className="border-t border-amber-200/40 pt-3 mt-3">
        <div className="text-[11px] uppercase tracking-wider font-medium text-muted-foreground mb-2">
          Testmail sturen naar
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
          <input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="uw-eigen-adres@uwbedrijf.nl"
            className="flex-1 px-3 py-2 rounded-md border border-border bg-white text-sm"
            data-testid="ec-email-test-input"
            autoComplete="email"
          />
          <button
            onClick={sendTest}
            disabled={testLoading || !testTo.trim()}
            className="btn-primary inline-flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm disabled:opacity-50"
            data-testid="ec-email-test-send-btn"
          >
            {testLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Send test email
          </button>
        </div>
        <p className="text-[11px] text-muted-foreground mt-1.5">
          Tip: gebruik een echt e-mailadres dat u zelf kunt openen. Adressen op example.com of test.com worden geblokkeerd — die geven Resend "Delivery Delayed".
        </p>
      </div>

      {lastTestResult && (
        <div
          className={`mt-3 text-xs rounded-md px-3 py-2 border ${lastTestResult.ok ? 'bg-emerald-50 border-emerald-200 text-emerald-900' : 'bg-rose-50 border-rose-200 text-rose-900'}`}
          data-testid="ec-email-test-result"
        >
          <div>{lastTestResult.msg}</div>
          {lastTestResult.data && (lastTestResult.data.used_from || lastTestResult.data.used_reply_to) && (
            <div className="mt-2 pt-2 border-t border-current/20 font-mono text-[11px] leading-relaxed" data-testid="ec-email-test-raw">
              <div>used_from: <span className="font-semibold">{lastTestResult.data.used_from || '∅'}</span></div>
              <div>used_reply_to: <span className="font-semibold">{lastTestResult.data.used_reply_to || '∅'}</span></div>
              {'contains_noreply' in (lastTestResult.data || {}) && (
                <div>
                  contains_noreply:{' '}
                  <span className={lastTestResult.data.contains_noreply ? 'text-rose-700 font-semibold' : 'text-emerald-700 font-semibold'}>
                    {String(lastTestResult.data.contains_noreply)}
                  </span>
                </div>
              )}
              {lastTestResult.data.provider_id && (
                <div>provider_id: <span className="font-semibold">{lastTestResult.data.provider_id}</span></div>
              )}
              {lastTestResult.data.provider_response && (
                <details className="mt-1">
                  <summary className="cursor-pointer text-muted-foreground">Ruwe provider response</summary>
                  <pre className="mt-1 whitespace-pre-wrap break-all text-[10px] bg-white/60 p-2 rounded border border-current/20">
                    {JSON.stringify(lastTestResult.data.provider_response, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}



function LeadRow({ lead, onStatusChange, expanded, onToggle }) {
  const status = lead.status || 'new';
  return (
    <div className="border border-border rounded-lg bg-white overflow-hidden" data-testid={`ec-lead-row-${lead.lead_id}`}>
      <div className="flex items-center justify-between gap-3 px-4 py-3 cursor-pointer hover:bg-muted/40" onClick={onToggle}>
        <div className="flex items-center gap-3 min-w-0 flex-1">
          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <TypePill type={lead.cta_type} />
              <StatusBadge status={status} />
              <span className="text-xs text-muted-foreground">{fmtDate(lead.created_at)}</span>
            </div>
            <div className="text-sm font-medium mt-1 truncate">{lead.name || '—'}</div>
            <div className="text-xs text-muted-foreground truncate">
              {lead.email}{lead.postcode ? ` · ${lead.postcode}` : ''}{lead.phone ? ` · ${lead.phone}` : ''}
            </div>
          </div>
        </div>
        <button className="text-muted-foreground hover:text-primary flex-shrink-0" data-testid={`ec-lead-toggle-${lead.lead_id}`}>
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>
      </div>

      {expanded && (
        <div className="border-t border-border bg-muted/20 px-4 py-3 space-y-3" data-testid={`ec-lead-details-${lead.lead_id}`}>
          {/* Quick actions */}
          <div className="flex flex-wrap gap-2">
            <button
              onClick={(e) => { e.stopPropagation(); copy(lead.email); }}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-border rounded-md hover:bg-white"
              data-testid={`ec-lead-copy-email-${lead.lead_id}`}
            >
              <Mail className="w-3 h-3" /> E-mail kopiëren
            </button>
            {lead.phone && (
              <button
                onClick={(e) => { e.stopPropagation(); copy(lead.phone); }}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-border rounded-md hover:bg-white"
                data-testid={`ec-lead-copy-phone-${lead.lead_id}`}
              >
                <Phone className="w-3 h-3" /> Telefoon kopiëren
              </button>
            )}
            {lead.intake_id && (
              <button
                onClick={(e) => { e.stopPropagation(); copy(lead.intake_id); }}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-border rounded-md hover:bg-white"
                data-testid={`ec-lead-copy-intake-${lead.lead_id}`}
              >
                <Copy className="w-3 h-3" /> Intake-id
              </button>
            )}
          </div>

          {/* Status workflow — depends on cta_type */}
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground">Status:</span>
            {(STATUS_WORKFLOW[lead.cta_type] || STATUS_WORKFLOW.installation).map((s) => (
              <button
                key={s}
                onClick={(e) => { e.stopPropagation(); if (s !== status) onStatusChange(lead.lead_id, s); }}
                className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                  status === s
                    ? `${STATUS_LABEL[s].cls} border-transparent`
                    : 'bg-white border-border text-muted-foreground hover:bg-muted'
                }`}
                data-testid={`ec-lead-set-${s}-${lead.lead_id}`}
              >
                {STATUS_LABEL[s].label}
              </button>
            ))}
          </div>

          {/* Phase 5: Starter-kit specific blocks */}
          {lead.cta_type === 'start_package' && lead.compatibility && (
            <div data-testid={`ec-lead-compat-${lead.lead_id}`}>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Compatibiliteit</div>
              <div className="text-xs bg-white border border-border rounded-md px-3 py-2 grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-1">
                <div><span className="text-muted-foreground">Meter:</span> {COMPAT_LABELS.meter_type[lead.compatibility.meter_type] || lead.compatibility.meter_type || '—'}</div>
                <div><span className="text-muted-foreground">Aansluiting:</span> {COMPAT_LABELS.phase_type[lead.compatibility.phase_type] || lead.compatibility.phase_type || '—'}</div>
                <div>
                  <span className="text-muted-foreground">
                    {lead.compatibility.live_insight ? 'Live inzicht:' : 'Zonne-opstelling:'}
                  </span>{' '}
                  {lead.compatibility.live_insight
                    ? COMPAT_LABELS.live_insight[lead.compatibility.live_insight] || lead.compatibility.live_insight
                    : COMPAT_LABELS.solar_setup[lead.compatibility.solar_setup] || lead.compatibility.solar_setup || '—'}
                </div>
              </div>
            </div>
          )}

          {lead.cta_type === 'start_package' && lead.address && (
            <div data-testid={`ec-lead-address-${lead.lead_id}`}>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Adres</div>
              <div className="text-xs bg-white border border-border rounded-md px-3 py-2">
                {lead.address.street || '—'}<br />
                {lead.address.postcode || ''} {lead.address.city || ''}
              </div>
            </div>
          )}

          {lead.cta_type === 'start_package' && lead.preference && (
            <div data-testid={`ec-lead-preference-${lead.lead_id}`}>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Gewenste route</div>
              <div className="text-xs bg-white border border-border rounded-md px-3 py-2 inline-flex">
                {PREF_LABELS[lead.preference] || lead.preference}
              </div>
            </div>
          )}

          {/* Phase 9.x — Interested product (when lead came from a product card) */}
          {(lead.compatibility?.interested_product_id || lead.interested_product_name) && (
            <div data-testid={`ec-lead-product-${lead.lead_id}`}>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
                Interesse in product
              </div>
              <div className="text-xs bg-white border border-border rounded-md px-3 py-2">
                {lead.interested_product_name && (
                  <div className="font-medium text-foreground">{lead.interested_product_name}</div>
                )}
                {lead.compatibility?.interested_product_id && (
                  <div className="text-muted-foreground font-mono text-[11px] mt-0.5">
                    id: {lead.compatibility.interested_product_id}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Phase 9.x — Mail status (customer confirmation + admin notification) */}
          {('mail_sent' in lead || lead.mail_error || lead.mail_provider_id) && (
            <div data-testid={`ec-lead-mail-status-${lead.lead_id}`}>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">
                Bevestigingsmail
              </div>
              <div
                className={`text-xs rounded-md px-3 py-2 border ${
                  lead.mail_sent
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                    : 'bg-amber-50 border-amber-200 text-amber-900'
                }`}
              >
                <div className="flex items-center justify-between gap-3">
                  <span className="font-medium">
                    {lead.mail_sent ? '✓ Verzonden naar klant' : '⚠ Niet verzonden'}
                  </span>
                  {lead.mail_provider_id && (
                    <span className="font-mono text-[11px] opacity-80" data-testid={`ec-lead-mail-provider-${lead.lead_id}`}>
                      {String(lead.mail_provider_id).slice(0, 8)}…
                    </span>
                  )}
                </div>
                {lead.mail_admin_provider_id && (
                  <div className="mt-1 text-[11px] opacity-80">
                    Admin-notificatie: <span className="font-mono">{String(lead.mail_admin_provider_id).slice(0, 8)}…</span>
                    {lead.mail_admin_recipient && <> → {lead.mail_admin_recipient}</>}
                  </div>
                )}
                {lead.mail_error && (
                  <div className="mt-1 text-[11px] font-mono break-all" data-testid={`ec-lead-mail-error-${lead.lead_id}`}>
                    {lead.mail_error}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Message */}
          {lead.message && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Bericht</div>
              <div className="text-sm bg-white border border-border rounded-md px-3 py-2 leading-relaxed whitespace-pre-wrap">
                {lead.message}
              </div>
            </div>
          )}

          {/* Intake summary */}
          {lead.intake_summary && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Intake snapshot</div>
              <div className="text-xs bg-white border border-border rounded-md px-3 py-2 grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                <div><span className="text-muted-foreground">Zonnepanelen:</span> {lead.intake_summary.solar_panels || '—'}</div>
                <div><span className="text-muted-foreground">Sturingsvoorkeur:</span> {lead.intake_summary.control_preference || '—'}</div>
                <div><span className="text-muted-foreground">Installatievoorkeur:</span> {lead.intake_summary.installation_preference || '—'}</div>
                <div><span className="text-muted-foreground">Taal:</span> {lead.intake_summary.language || '—'}</div>
                <div className="sm:col-span-2">
                  <span className="text-muted-foreground">Grootverbruikers:</span>{' '}
                  {(lead.intake_summary.appliances_present || []).join(', ') || '—'}
                </div>
              </div>
            </div>
          )}

          <div className="text-[11px] text-muted-foreground">
            Lead-id: <span className="font-mono">{lead.lead_id}</span>
            {lead.status_updated_at && <span> · status gewijzigd: {fmtDate(lead.status_updated_at)}</span>}
          </div>
        </div>
      )}
    </div>
  );
}

export default function EnergyControlLeadsTab({ token }) {
  const [leads, setLeads] = useState([]);
  const [counts, setCounts] = useState({ counts_by_status: {}, counts_by_type: {}, total: 0 });
  const [loading, setLoading] = useState(false);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  // Test-data hider — default ON so the owner sees only real leads. The
  // toggle reveals pytest/rate-limit/test_rl rows for debugging without
  // ever deleting them from the database.
  const [showTestData, setShowTestData] = useState(false);

  const fetchLeads = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = {};
      if (typeFilter !== 'all') params.cta_type = typeFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      const res = await axios.get(`${API}/admin/energy-control/leads`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setLeads(res.data.leads || []);
      setCounts({
        counts_by_status: res.data.counts_by_status || {},
        counts_by_type: res.data.counts_by_type || {},
        total: res.data.total || 0,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Kon leads niet laden');
    } finally {
      setLoading(false);
    }
  }, [token, typeFilter, statusFilter]);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const onStatusChange = async (leadId, status) => {
    try {
      await axios.patch(`${API}/admin/energy-control/leads/${leadId}`,
        { status },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(`Status gewijzigd: ${STATUS_LABEL[status].label}`);
      // Optimistic update
      setLeads((prev) => prev.map((l) => l.lead_id === leadId ? { ...l, status } : l));
      // Refetch counts
      fetchLeads();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Status-update mislukt');
    }
  };

  const TYPE_FILTERS = [
    { value: 'all', label: 'Alle' },
    { value: 'start_package', label: 'Startpakket' },
    { value: 'installation', label: 'Installatie' },
    { value: 'monitoring', label: 'Monitoring' },
  ];

  const STATUS_FILTERS = [
    { value: 'all', label: 'Alle' },
    { value: 'new', label: 'New' },
    { value: 'contacted', label: 'Contacted' },
    { value: 'reviewed', label: 'Reviewed' },
    { value: 'packaged', label: 'Packaged' },
    { value: 'shipped', label: 'Shipped' },
    { value: 'won', label: 'Won' },
    { value: 'lost', label: 'Lost' },
    { value: 'cancelled', label: 'Cancelled' },
  ];

  return (
    <div data-testid="ec-leads-tab">
      {/* Email diagnostics + test email button */}
      <EmailDiagnosticsPanel token={token} />

      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-5">
        {['new', 'contacted', 'reviewed', 'packaged'].map((s) => (
          <div key={s} className="bg-white border border-border rounded-lg px-3 py-3">
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{STATUS_LABEL[s].label}</div>
            <div className="text-xl font-semibold text-primary mt-0.5" data-testid={`ec-leads-count-${s}`}>
              {counts.counts_by_status[s] ?? 0}
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white border border-border rounded-lg p-3 mb-4 space-y-3" data-testid="ec-leads-filters">
        <div className="flex flex-wrap items-center gap-2">
          <Filter className="w-4 h-4 text-muted-foreground" />
          <span className="text-xs text-muted-foreground mr-1">Type:</span>
          {TYPE_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setTypeFilter(f.value)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                typeFilter === f.value ? 'bg-primary text-white border-primary' : 'bg-muted/50 border-border text-foreground hover:bg-muted'
              }`}
              data-testid={`ec-filter-type-${f.value}`}
            >
              {f.label}
              {f.value !== 'all' && counts.counts_by_type[f.value] != null && (
                <span className="ml-1 opacity-70">({counts.counts_by_type[f.value]})</span>
              )}
            </button>
          ))}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground mr-1 ml-5">Status:</span>
          {STATUS_FILTERS.map((f) => (
            <button
              key={f.value}
              onClick={() => setStatusFilter(f.value)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-medium border transition-colors ${
                statusFilter === f.value ? 'bg-primary text-white border-primary' : 'bg-muted/50 border-border text-foreground hover:bg-muted'
              }`}
              data-testid={`ec-filter-status-${f.value}`}
            >
              {f.label}
            </button>
          ))}
          <div className="flex-1" />
          <label className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground select-none cursor-pointer"
            data-testid="ec-leads-show-testdata-toggle">
            <input
              type="checkbox"
              checked={showTestData}
              onChange={(e) => setShowTestData(e.target.checked)}
              className="accent-emerald-600"
              data-testid="ec-leads-show-testdata-input"
            />
            Testdata tonen
          </label>
          <button
            onClick={fetchLeads}
            disabled={loading}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs border border-border rounded-md hover:bg-muted disabled:opacity-50"
            data-testid="ec-leads-refresh"
          >
            <RefreshCw className={`w-3 h-3 ${loading ? 'animate-spin' : ''}`} /> Vernieuwen
          </button>
        </div>
      </div>

      {/* Leads list */}
      {(() => {
        const visibleLeads = showTestData ? leads : leads.filter((l) => !isTestLead(l));
        const hiddenCount = leads.length - visibleLeads.length;
        if (loading) {
          return (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-5 h-5 animate-spin text-primary" />
            </div>
          );
        }
        if (visibleLeads.length === 0) {
          return (
            <div className="text-center py-12 bg-white border border-dashed border-border rounded-lg" data-testid="ec-leads-empty">
              <p className="text-sm text-muted-foreground">
                {hiddenCount > 0
                  ? `Geen echte leads gevonden. ${hiddenCount} testlead${hiddenCount === 1 ? '' : 's'} verborgen.`
                  : 'Geen leads gevonden voor de huidige filters.'}
              </p>
            </div>
          );
        }
        return (
          <div className="space-y-2" data-testid="ec-leads-list">
            {hiddenCount > 0 && !showTestData && (
              <div className="text-[11px] text-muted-foreground px-1" data-testid="ec-leads-hidden-count">
                {hiddenCount} testlead{hiddenCount === 1 ? '' : 's'} verborgen.
                Zet &ldquo;Testdata tonen&rdquo; aan om ze te zien.
              </div>
            )}
            {visibleLeads.map((lead) => (
              <LeadRow
                key={lead.lead_id}
                lead={lead}
                expanded={expandedId === lead.lead_id}
                onToggle={() => setExpandedId(expandedId === lead.lead_id ? null : lead.lead_id)}
                onStatusChange={onStatusChange}
              />
            ))}
          </div>
        );
      })()}
    </div>
  );
}
